//mar11 smartListsSaveUtils.js without column saves
// src/utils/smartListsSaveUtils.js
// -----------------------------------------------------------------------------
// SmartLists draft normalization + canonical payload builder
//
// PURPOSE
//  • Keep SmartLists spreadsheet normalization OUT of page/components
//  • Preserve instant local spreadsheet UX by making draft/canonical transforms
//    pure, synchronous, and side-effect free
//  • Normalize only MEANINGFUL rows/groups for persistence
//  • Drop padded blank rows from draft payloads
//  • Preserve stable option identity (optionId) for persisted rows
//  • Generate canonical save payloads for org-managed field docs + option-set docs
//
// IMPORTANT ARCHITECTURAL RULES
//  • This file does NOT read/write Firestore
//  • This file does NOT read/write browser storage
//  • This file does NOT know about React state
//  • This file does NOT mutate its inputs
//  • This file does NOT own debounce timing
//
// WORKFLOW ALIGNMENT
//  • Immediate path:
//      - local canvas state updates instantly
//      - this file is NOT involved in the hot typing path unless the caller
//        chooses to use it for cheap dirty checks
//
//  • Background draft-save path:
//      - caller passes dirty groups/rows into these helpers
//      - helpers normalize sparse meaningful payloads only
//
//  • Explicit Save path:
//      - caller passes current workspace + canonical registry data
//      - helpers build canonical managed-field / managed-option-set write payloads
//
// DATA MODEL ALIGNMENT
//  • Canonical managed field docs:
//      organizations/{orgId}/managedFields/{managedFieldId}
//
//  • Canonical managed option set docs:
//      organizations/{orgId}/managedOptionSets/{optionsSetId}
//
// NOTES
//  • We intentionally keep `rowId` as a local/workspace identity.
//  • We intentionally treat `optionId` as canonical persisted identity.
//  • For NEW meaningful rows without optionId:
//      - draft payload keeps optionId as null
//      - canonical save payload generates optionId at save time
//
// -----------------------------------------------------------------------------

import { v4 as uuidv4 } from 'uuid';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

export const SMARTLISTS_ALLOWED_FIELD_TYPES = Object.freeze([
  'single-select',
  'multi-select',
]);

export const SMARTLISTS_ALLOWED_FIELD_STATUS = Object.freeze([
  'draft',
  'active',
  'archived',
]);

export const SMARTLISTS_ALLOWED_LIFECYCLE_STATUS = Object.freeze([
  '',
  'active',
  'inactive',
  'pending',
  'archived',
]);

// -----------------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------------

const logPrefix = '[smartListsDraftUtils]';

const normalizeString = (value, fallback = '') => {
  return typeof value === 'string' ? value.trim() : fallback;
};

const normalizeNullableString = (value) => {
  const normalized = normalizeString(value, '');
  return normalized.length > 0 ? normalized : null;
};

const isObject = (value) => {
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

const isMeaningfulBoolean = (value) => value === true;

const cloneJsonSafe = (value) => {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const toIsoDateOnly = (value) => {
  if (!value) return null;

  // Firestore Timestamp support
  if (typeof value?.toDate === 'function') {
    const asDate = value.toDate();
    if (asDate instanceof Date && !Number.isNaN(asDate.getTime())) {
      return asDate.toISOString().slice(0, 10);
    }
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // already canonical yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    // mm/dd/yyyy
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, mmRaw, ddRaw, yyyyRaw] = slashMatch;
      const mm = Number(mmRaw);
      const dd = Number(ddRaw);
      const yyyy = Number(yyyyRaw);

      if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1000) {
        return null;
      }

      const candidate = new Date(Date.UTC(yyyy, mm - 1, dd));
      if (
        candidate.getUTCFullYear() !== yyyy ||
        candidate.getUTCMonth() !== mm - 1 ||
        candidate.getUTCDate() !== dd
      ) {
        return null;
      }

      return `${String(yyyy).padStart(4, '0')}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    }

    // final best-effort parse
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString().slice(0, 10);
  }

  return null;
};

const normalizeFieldType = (value) => {
  const normalized = normalizeString(value, 'single-select');

  if (SMARTLISTS_ALLOWED_FIELD_TYPES.includes(normalized)) {
    return normalized;
  }

  console.warn(`${logPrefix} Unsupported field type received; defaulting to single-select.`, {
    received: value,
    normalized,
  });

  return 'single-select';
};

const normalizeFieldStatus = (value) => {
  const normalized = normalizeString(value, 'draft');

  if (SMARTLISTS_ALLOWED_FIELD_STATUS.includes(normalized)) {
    return normalized;
  }

  console.warn(`${logPrefix} Unsupported field status received; defaulting to draft.`, {
    received: value,
    normalized,
  });

  return 'draft';
};

const normalizeLifecycleStatus = (value) => {
  const normalized = normalizeString(value, '').toLowerCase();

  if (SMARTLISTS_ALLOWED_LIFECYCLE_STATUS.includes(normalized)) {
    return normalized;
  }

  console.warn(`${logPrefix} Unsupported lifecycleStatus received; normalizing to empty string.`, {
    received: value,
    normalized,
  });

  return '';
};

const normalizeCanonicalLifecycleStatusForSave = (value) => {
  const normalized = normalizeLifecycleStatus(value);

  // IMPORTANT:
  // - draft/workspace rows may legitimately carry '' while the user is editing
  // - canonical managed option payloads should never persist blank lifecycleStatus
  // - blank/unset canonical status means active
  return normalized || 'active';
};

const normalizeGovernance = (governance = {}) => ({
  allowLocalLabelOverride:
    governance?.allowLocalLabelOverride === true,
  allowLocalRequiredOverride:
    governance?.allowLocalRequiredOverride === true,
  allowLocalHelperTextOverride:
    governance?.allowLocalHelperTextOverride === true,
  allowLocalPlaceholderOverride:
    governance?.allowLocalPlaceholderOverride === true,
  allowLocalOptionEditing:
    governance?.allowLocalOptionEditing === true,
  allowLocalDependencies:
    governance?.allowLocalDependencies === true,
  allowOther:
    governance?.allowOther === true,
});

const normalizeDisplayDefaults = (displayDefaults = {}) => ({
  placeholder: normalizeString(displayDefaults?.placeholder, ''),
  helperText: normalizeString(displayDefaults?.helperText, ''),
});

const mapRowValueFallback = (row = {}) => {
  const explicit = normalizeString(row?.value, '');
  if (explicit) return explicit;

  const label = normalizeString(row?.label, '');
  return label;
};

// -----------------------------------------------------------------------------
// Row-level helpers
// -----------------------------------------------------------------------------

/**
 * A SmartLists row is meaningful if it contains any real business data.
 *
 * IMPORTANT
 *  • We intentionally do NOT treat rowId as meaningful content
 *  • We intentionally do NOT treat dirty/isNew flags as meaningful content
 *  • Padded blank rows should fail this check
 */
export const isMeaningfulSmartListRow = (row = {}) => {
  if (!row || typeof row !== 'object') return false;

  return Boolean(
    normalizeString(row?.label, '') ||
    normalizeString(row?.hint, '') ||
    normalizeString(row?.value, '') ||
    normalizeString(row?.lifecycleStatus, '') ||
    toIsoDateOnly(row?.liveFrom) ||
    toIsoDateOnly(row?.liveTo) ||
    normalizeString(row?.optionId, '')
  );
};

/**
 * Normalize a workspace row into a stable local/edit payload.
 *
 * USE CASES
 *  • local dirty tracking
 *  • draft save normalization
 *  • canonical save normalization foundation
 */
export const normalizeSmartListRow = (row = {}) => {
  const normalizedRow = {
    rowId: normalizeString(row?.rowId, `smartlists-row-${uuidv4()}`),
    optionId: normalizeNullableString(row?.optionId),
    label: normalizeString(row?.label, ''),
    value: mapRowValueFallback(row),
    hint: normalizeString(row?.hint, ''),
    lifecycleStatus: normalizeLifecycleStatus(row?.lifecycleStatus),
    liveFrom: toIsoDateOnly(row?.liveFrom),
    liveTo: toIsoDateOnly(row?.liveTo),

    isPendingRequest: row?.isPendingRequest === true,
    requestId: normalizeNullableString(row?.requestId),

    dirty: row?.dirty === true,
    isNew: row?.isNew === true,
    isDeleted: row?.isDeleted === true,
  };

  return {
    ...normalizedRow,
    isBlankRow: !isMeaningfulSmartListRow(normalizedRow),
  };
};

/**
 * Normalize a draft row payload.
 *
 * RULES
 *  • Drops padded blank rows from persistence
 *  • Keeps deleted persisted rows as tombstones
 *  • Keeps new meaningful rows without forcing optionId generation yet
 */
export const normalizeSmartListDraftRow = (row = {}) => {
  const normalized = normalizeSmartListRow(row);

  const isMeaningful = isMeaningfulSmartListRow(normalized);
  const hasCanonicalIdentity = !!normalized.optionId;

  // Deleted persisted row must survive into draft as a tombstone.
  if (normalized.isDeleted && hasCanonicalIdentity) {
    return {
      rowId: normalized.rowId,
      optionId: normalized.optionId,
      label: normalized.label,
      value: normalized.value,
      hint: normalized.hint,
      lifecycleStatus: normalized.lifecycleStatus,
      liveFrom: normalized.liveFrom,
      liveTo: normalized.liveTo,
      isPendingRequest: normalized.isPendingRequest,
      requestId: normalized.requestId,
      dirty: normalized.dirty,
      isNew: normalized.isNew,
      isDeleted: true,
    };
  }

  // New blank row or padded blank row should not be persisted.
  if (!isMeaningful) {
    return null;
  }

  return {
    rowId: normalized.rowId,
    optionId: normalized.optionId,
    label: normalized.label,
    value: normalized.value,
    hint: normalized.hint,
    lifecycleStatus: normalized.lifecycleStatus,
    liveFrom: normalized.liveFrom,
    liveTo: normalized.liveTo,
    isPendingRequest: normalized.isPendingRequest,
    requestId: normalized.requestId,
    dirty: normalized.dirty,
    isNew: normalized.isNew,
    isDeleted: false,
  };
};

/**
 * Normalize a canonical row payload for managedOptionSets.options.
 *
 * RULES
 *  • Generates optionId only when caller requests canonical normalization
 *  • Preserves stable optionId when already present
 *  • Never emits padded blank rows
 *  • Canonical lifecycleStatus never remains blank:
 *      - blank/unset normalizes to active
 *      - canonical values remain:
 *          active / inactive / pending / archived
 */
export const normalizeSmartListCanonicalOptionRow = (
  row = {},
  {
    generateOptionIdIfMissing = true,
    fallbackOrder = 1,
  } = {}
) => {
  const normalized = normalizeSmartListRow(row);

  if (normalized.isDeleted) {
    return null;
  }

  if (!isMeaningfulSmartListRow(normalized)) {
    return null;
  }

  const optionId =
    normalized.optionId ||
    (generateOptionIdIfMissing ? uuidv4() : null);

  if (!optionId) {
    return null;
  }

  return {
    optionId,
    label: normalized.label,
    value: normalized.value || normalized.label || '',
    hint: normalized.hint || '',
    order: Number.isFinite(Number(row?.order)) ? Number(row.order) : fallbackOrder,
    lifecycleStatus: normalizeCanonicalLifecycleStatusForSave(normalized.lifecycleStatus),
    liveFrom: normalized.liveFrom,
    liveTo: normalized.liveTo,
  };
};

// -----------------------------------------------------------------------------
// Group-level helpers
// -----------------------------------------------------------------------------

/**
 * Normalize the field/settings patch for a SmartLists workspace item.
 *
 * IMPORTANT
 *  • This is intentionally narrow and schema-aligned
 *  • Caller can later compare this patch against canonical field docs
 */
export const normalizeSmartListFieldPatch = (field = {}) => {
  return {
    label: normalizeString(field?.label, 'New SmartList Field'),
    description: normalizeString(field?.description, ''),
    type: normalizeFieldType(field?.type),
    status: normalizeFieldStatus(field?.status),
    governance: normalizeGovernance(field?.governance || {}),
    displayDefaults: normalizeDisplayDefaults(field?.displayDefaults || {}),
  };
};

/**
 * Normalize a SmartLists workspace item/group for draft persistence.
 *
 * INPUT SHAPE
 *  • expected to align with SmartListsPage / SmartListsWorkspaceCanvas items:
 *      {
 *        localDraftId,
 *        managedFieldId,
 *        optionsSetId,
 *        isNew,
 *        isDirty,
 *        persisted,
 *        field,
 *        optionRows
 *      }
 */
export const normalizeSmartListDraftGroup = (
  group = {},
  {
    dirtyRowIds = null,
    includeAllRows = false,
    includeFieldPatch = true,
  } = {}
) => {
  const localDraftId = normalizeString(group?.localDraftId);
  if (!localDraftId) {
    console.warn(`${logPrefix} Skipping draft group with missing localDraftId.`, { group });
    return null;
  }

  const dirtyRowIdSet =
    dirtyRowIds instanceof Set
      ? dirtyRowIds
      : Array.isArray(dirtyRowIds)
        ? new Set(dirtyRowIds)
        : null;

  const sourceRows = Array.isArray(group?.optionRows) ? group.optionRows : [];

  const normalizedRows = sourceRows
    .filter((row) => {
      if (includeAllRows) return true;
      if (!dirtyRowIdSet) return true;

      const rowId = normalizeString(row?.rowId, '');
      return dirtyRowIdSet.has(rowId);
    })
    .map((row) => normalizeSmartListDraftRow(row))
    .filter(Boolean);

  const fieldPatch = includeFieldPatch
    ? normalizeSmartListFieldPatch(group?.field || {})
    : null;

  const hasMeaningfulRows = normalizedRows.length > 0;
  const hasMeaningfulFieldPatch = includeFieldPatch && !!fieldPatch;

  // We keep group-level shell if:
  // - it is a new local draft, or
  // - it has meaningful rows, or
  // - it has a meaningful field patch, or
  // - it represents an existing canonical item marked dirty
  const shouldPersistGroupShell =
    group?.isNew === true ||
    group?.isDirty === true ||
    hasMeaningfulRows ||
    hasMeaningfulFieldPatch;

  if (!shouldPersistGroupShell) {
    return null;
  }

  return {
    localDraftId,
    managedFieldId: normalizeNullableString(group?.managedFieldId),
    managedOptionSetId: normalizeNullableString(group?.optionsSetId),
    isNew: group?.isNew === true,
    isDirty: group?.isDirty === true,
    persisted: group?.persisted === true,
    fieldPatch: fieldPatch || null,
    rows: normalizedRows,
  };
};

// -----------------------------------------------------------------------------
// Draft payload builders
// -----------------------------------------------------------------------------

/**
 * Build a sparse draft payload for Firestore draft persistence.
 *
 * EXPECTED INPUTS
 *  • workspaceItems                 -> current workspace groups/items
 *  • dirtyGroupIds                  -> Set<string> | string[]
 *  • dirtyRowsByGroup               -> { [groupId]: Set<string> | string[] }
 *  • selectedDraftId                -> optional UI state
 *  • uiState                        -> optional persisted workspace UI data
 *
 * OUTPUT
 *  • contains only dirty/meaningful groups and rows
 *  • contains no padded blank rows
 */
export const buildSmartListsDraftPayload = ({
  organizationId,
  editedBy,
  workspaceItems = [],
  dirtyGroupIds = null,
  dirtyRowsByGroup = {},
  selectedDraftId = null,
  uiState = {},
} = {}) => {
  const normalizedOrganizationId = normalizeString(organizationId);
  const normalizedEditedBy = normalizeString(editedBy);

  if (!normalizedOrganizationId) {
    throw new Error(`${logPrefix} organizationId is required to build draft payload.`);
  }

  if (!normalizedEditedBy) {
    throw new Error(`${logPrefix} editedBy is required to build draft payload.`);
  }

  const dirtyGroupIdSet =
    dirtyGroupIds instanceof Set
      ? dirtyGroupIds
      : Array.isArray(dirtyGroupIds)
        ? new Set(dirtyGroupIds)
        : null;

  const groups = {};

  (Array.isArray(workspaceItems) ? workspaceItems : []).forEach((group) => {
    const localDraftId = normalizeString(group?.localDraftId, '');
    if (!localDraftId) return;

    const groupIsDirty =
      dirtyGroupIdSet
        ? dirtyGroupIdSet.has(localDraftId)
        : (group?.isDirty === true || group?.isNew === true);

    const dirtyRows =
      dirtyRowsByGroup?.[localDraftId] || null;

    if (!groupIsDirty && !dirtyRows) {
      return;
    }

    const normalizedGroup = normalizeSmartListDraftGroup(group, {
      dirtyRowIds: dirtyRows,
      includeAllRows: !dirtyRows,
      includeFieldPatch: true,
    });

    if (!normalizedGroup) {
      return;
    }

    groups[localDraftId] = normalizedGroup;
  });

  return {
    organizationId: normalizedOrganizationId,
    editedBy: normalizedEditedBy,
    updatedAtClient: new Date().toISOString(),
    selectedDraftId: normalizeNullableString(selectedDraftId),
    uiState: {
      columnWidths: isObject(uiState?.columnWidths)
        ? cloneJsonSafe(uiState.columnWidths)
        : null,
      requestedVisibleRowCount: Number.isFinite(Number(uiState?.requestedVisibleRowCount))
        ? Number(uiState.requestedVisibleRowCount)
        : null,
      sidebarMode: normalizeNullableString(uiState?.sidebarMode),
    },
    groups,
  };
};

// -----------------------------------------------------------------------------
// Canonical save payload builders
// -----------------------------------------------------------------------------

/**
 * Build a lookup from current workspace items by localDraftId.
 */
export const buildSmartListsWorkspaceIndex = (workspaceItems = []) => {
  return (Array.isArray(workspaceItems) ? workspaceItems : []).reduce((acc, item) => {
    const key = normalizeString(item?.localDraftId, '');
    if (!key) return acc;
    acc[key] = item;
    return acc;
  }, {});
};

/**
 * Build a lookup from canonical managed option sets by id.
 */
export const buildManagedOptionSetIndex = (managedOptionSets = []) => {
  return (Array.isArray(managedOptionSets) ? managedOptionSets : []).reduce((acc, item) => {
    const id = normalizeString(item?.id, '');
    if (!id) return acc;
    acc[id] = item;
    return acc;
  }, {});
};

/**
 * Build a lookup from canonical managed fields by id.
 */
export const buildManagedFieldIndex = (managedFields = []) => {
  return (Array.isArray(managedFields) ? managedFields : []).reduce((acc, item) => {
    const id = normalizeString(item?.id, '');
    if (!id) return acc;
    acc[id] = item;
    return acc;
  }, {});
};

const resolveCanonicalSmartListIds = (group = {}) => {
  const existingManagedFieldId = normalizeNullableString(group?.managedFieldId);
  const existingOptionsSetId = normalizeNullableString(group?.optionsSetId);

  return {
    managedFieldId: existingManagedFieldId || uuidv4(),
    optionsSetId: existingOptionsSetId || uuidv4(),
    generatedManagedFieldId: !existingManagedFieldId,
    generatedOptionsSetId: !existingOptionsSetId,
  };
};

/**
 * Build canonical managed option rows from a workspace group.
 *
 * POLICY
 *  • Existing canonical optionIds are preserved
 *  • New meaningful rows get optionIds at save time
 *  • Blank rows are dropped
 *  • Deleted rows are omitted from the next options array
 *
 * NOTE
 *  • This helper builds the next full canonical options array for a set.
 *  • That keeps save application deterministic.
 *  • Lifecycle vocabulary is shared with the canonical domain model:
 *      - active
 *      - inactive
 *      - pending
 *      - archived
 *  • Blank/unset lifecycleStatus is normalized to active for canonical save.
 */
export const buildCanonicalManagedOptionRowsFromGroup = (group = {}) => {
  const sourceRows = Array.isArray(group?.optionRows) ? group.optionRows : [];

  const nextOptions = sourceRows
    .map((row, index) =>
      normalizeSmartListCanonicalOptionRow(row, {
        generateOptionIdIfMissing: true,
        fallbackOrder: index + 1,
      })
    )
    .filter(Boolean)
    .map((row, index) => ({
      ...row,
      order: index + 1,
    }));

  return nextOptions;
};

/**
 * Build a canonical managedField patch from a workspace group.
 *
 * IMPORTANT
 *  • This is intentionally additive and narrow
 *  • It aligns to managedFields.js normalizeManagedFieldDoc expectations
 */
export const buildCanonicalManagedFieldPatchFromGroup = (group = {}) => {
  const fieldPatch = normalizeSmartListFieldPatch(group?.field || {});

  const normalizedOrder = Number.isFinite(Number(group?.order))
    ? Number(group.order)
    : null;

  return {
    label: fieldPatch.label,
    description: fieldPatch.description,
    type: fieldPatch.type,
    status: fieldPatch.status === 'draft' ? 'active' : fieldPatch.status,
    governance: fieldPatch.governance,
    displayDefaults: fieldPatch.displayDefaults,

    // IMPORTANT:
    // Preserve SmartLists group ordering as canonical managed-field order.
    // This is what lets the SmartLists sidepanel order and canvas column order
    // survive explicit Save and later reloads.
    ...(normalizedOrder !== null ? { order: normalizedOrder } : {}),
  };
};
/**
 * Build the canonical save plan for SmartLists explicit Save.
 *
 * OUTPUT
 *  {
 *    managedFieldWrites: [
 *      {
 *        managedFieldId,
 *        isCreate,
 *        patch
 *      }
 *    ],
 *    managedOptionSetWrites: [
 *      {
 *        optionsSetId,
 *        isCreate,
 *        patch
 *      }
 *    ],
 *    diagnostics: { ... }
 *  }
 *
 * IMPORTANT
 *  • This helper does NOT choose Firestore APIs
 *  • It only constructs normalized deterministic write instructions
 */
export const buildSmartListsCanonicalSavePlan = ({
  workspaceItems = [],
  dirtyGroupIds = null,
  managedFields = [],
  managedOptionSets = [],
} = {}) => {
  const dirtyGroupIdSet =
    dirtyGroupIds instanceof Set
      ? dirtyGroupIds
      : Array.isArray(dirtyGroupIds)
        ? new Set(dirtyGroupIds)
        : null;

  const managedFieldIndex = buildManagedFieldIndex(managedFields);
  const managedOptionSetIndex = buildManagedOptionSetIndex(managedOptionSets);

  const managedFieldWrites = [];
  const managedOptionSetWrites = [];

  const diagnostics = {
    totalWorkspaceItems: Array.isArray(workspaceItems) ? workspaceItems.length : 0,
    totalDirtyGroups: dirtyGroupIdSet ? dirtyGroupIdSet.size : null,
    skippedGroups: [],
    fieldWrites: [],
    optionSetWrites: [],
  };

  (Array.isArray(workspaceItems) ? workspaceItems : []).forEach((group) => {
    const localDraftId = normalizeString(group?.localDraftId, '');
    if (!localDraftId) {
      diagnostics.skippedGroups.push({
        reason: 'missing_localDraftId',
      });
      return;
    }

    const shouldInclude =
      dirtyGroupIdSet
        ? dirtyGroupIdSet.has(localDraftId)
        : (group?.isDirty === true || group?.isNew === true);

    if (!shouldInclude) {
      return;
    }

    const {
      managedFieldId,
      optionsSetId,
      generatedManagedFieldId,
      generatedOptionsSetId,
    } = resolveCanonicalSmartListIds(group);

    const canonicalOptions = buildCanonicalManagedOptionRowsFromGroup(group);
    const fieldPatch = buildCanonicalManagedFieldPatchFromGroup(group);

    const optionSetPatch = {
      // Keep option set label aligned with the SmartList field label for now.
      // This matches the MVP behavior where the field and option set are created together.
      label: normalizeString(group?.field?.label, 'Managed option set'),
      options: canonicalOptions,
    };

    managedOptionSetWrites.push({
      optionsSetId,
      isCreate: generatedOptionsSetId || !managedOptionSetIndex[optionsSetId],
      patch: optionSetPatch,
      localDraftId,
      managedFieldId,
    });

    diagnostics.optionSetWrites.push({
      localDraftId,
      optionsSetId,
      optionCount: canonicalOptions.length,
      isCreate: generatedOptionsSetId || !managedOptionSetIndex[optionsSetId],
      generatedOptionsSetId,
    });

    managedFieldWrites.push({
      managedFieldId,
      isCreate: generatedManagedFieldId || !managedFieldIndex[managedFieldId],
      patch: {
        ...fieldPatch,
        optionsSource: {
          type: 'managedOptionSet',
          optionsSetId,
        },
      },
      localDraftId,
      optionsSetId,
    });

    diagnostics.fieldWrites.push({
      localDraftId,
      managedFieldId,
      optionsSetId,
      order: Number.isFinite(Number(group?.order)) ? Number(group.order) : null,
      isCreate: generatedManagedFieldId || !managedFieldIndex[managedFieldId],
      generatedManagedFieldId,
    });
  });

  return {
    managedFieldWrites,
    managedOptionSetWrites,
    diagnostics,
  };
};

// -----------------------------------------------------------------------------
// Dirty-state helpers
// -----------------------------------------------------------------------------

/**
 * Lightweight helper for callers that want to compare row snapshots cheaply.
 */
export const areSmartListRowsEquivalent = (rowA = {}, rowB = {}) => {
  const a = normalizeSmartListRow(rowA);
  const b = normalizeSmartListRow(rowB);

  return (
    a.optionId === b.optionId &&
    a.label === b.label &&
    a.value === b.value &&
    a.hint === b.hint &&
    a.lifecycleStatus === b.lifecycleStatus &&
    a.liveFrom === b.liveFrom &&
    a.liveTo === b.liveTo &&
    a.isDeleted === b.isDeleted
  );
};

/**
 * Returns true if a group has at least one meaningful row worth drafting/saving.
 */
export const smartListGroupHasMeaningfulRows = (group = {}) => {
  const rows = Array.isArray(group?.optionRows) ? group.optionRows : [];
  return rows.some((row) => {
    const normalized = normalizeSmartListRow(row);
    return normalized.isDeleted === true || isMeaningfulSmartListRow(normalized);
  });
};

/**
 * Returns a compact diagnostics snapshot for logging and tests.
 */
export const buildSmartListsNormalizationDiagnostics = ({
  workspaceItems = [],
  dirtyGroupIds = null,
  dirtyRowsByGroup = {},
} = {}) => {
  const dirtyGroupIdSet =
    dirtyGroupIds instanceof Set
      ? dirtyGroupIds
      : Array.isArray(dirtyGroupIds)
        ? new Set(dirtyGroupIds)
        : null;

  return (Array.isArray(workspaceItems) ? workspaceItems : []).map((group) => {
    const localDraftId = normalizeString(group?.localDraftId, '');
    const rows = Array.isArray(group?.optionRows) ? group.optionRows : [];
    const meaningfulRowCount = rows.filter((row) =>
      isMeaningfulSmartListRow(normalizeSmartListRow(row))
    ).length;

    const deletedRowCount = rows.filter((row) =>
      normalizeSmartListRow(row).isDeleted === true
    ).length;

    const dirtyRows =
      dirtyRowsByGroup?.[localDraftId] instanceof Set
        ? dirtyRowsByGroup[localDraftId].size
        : Array.isArray(dirtyRowsByGroup?.[localDraftId])
          ? dirtyRowsByGroup[localDraftId].length
          : 0;

    return {
      localDraftId,
      managedFieldId: normalizeNullableString(group?.managedFieldId),
      optionsSetId: normalizeNullableString(group?.optionsSetId),
      isDirty: dirtyGroupIdSet ? dirtyGroupIdSet.has(localDraftId) : group?.isDirty === true,
      totalRows: rows.length,
      meaningfulRowCount,
      deletedRowCount,
      dirtyRowCount: dirtyRows,
    };
  });
};